/**
 * 旗子时间对齐：把服务端时钟(serverTime)的时间戳换算到播放轴用的车端时钟(clientTime)。
 *
 * 背景：
 *  - 进度条(min/max、currentTime)用的是车端 vehicleInfo 的时间，即 clientTime（1hz）；
 *  - 旗子(operateTime)等来自大数据 dataview 的时间是服务端时间，即 serverTime（5hz）；
 *  两个时钟存在系统性偏差 offset = serverTime - clientTime，
 *  所以旗子落在播放轴上的位置应该是：operateTime - offset。
 *
 * offset 的采样有两路来源（都由服务端透传）：
 *  1. ws：FrameData.extra_info（JSON 字符串，优先用），
 *     serverTime 取 serverTimestamp（纳秒），clientTime 取 timestamp（毫秒）
 *  2. http：getPlayInfo 的 statusInfos[]，serverTime 取 extras.serverTimestamp，
 *     clientTime 取 msgTime（1hz，且只到秒，作为兜底）
 *
 * 调试：浏览器 Console 里可直接用 window.__timeAlign 查看采样与计算结果。
 */
import { ref } from 'vue';

/** 按归一后的 serverTime 去重，保留同一秒内不同的 WS 采样 */
const wsSamples = new Map();
const httpSamples = new Map();

let wsSorted = null;
let httpSorted = null;

/** ws 采样与目标时间相差超过这个范围(ms)就认为不可用，回退到 http 采样 */
const WS_SAMPLE_MAX_DISTANCE = 60 * 1000;

/** 采样变化的版本号，组件可以 watch 它来重新计算旗子位置（ws 数据是边下载边到的） */
export const offsetSampleVersion = ref(0);
let sampleBroadcastTimer = null;

/** 原始 extra_info 字符串，仅用于排查服务端到底给了什么字段 */
const extraInfoRawSamples = [];
let extraInfoLogCount = 0;
const EXTRA_INFO_LOG_LIMIT = 3;

/**
 * 时间戳归一到毫秒。服务端这些字段的单位并不统一（秒/毫秒/微秒/纳秒都出现过），
 * 这里按数量级判断。
 * @param {*} value
 * @returns {number|null} 毫秒
 */
export function toMs(value) {
  if (value === null || value === undefined || value === '') return null;
  let num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) {
    // 形如 '2026-10-29 17:10:00' 的时间字符串
    const parsed = Date.parse(String(value).replace(/-/g, '/'));
    return Number.isNaN(parsed) ? null : parsed;
  }
  num = Math.abs(num);
  if (num === 0) return null;
  if (num >= 1e17) return value / 1e6; // 纳秒
  if (num >= 1e14) return value / 1e3; // 微秒
  if (num >= 1e11) return Number(value); // 毫秒
  if (num >= 1e8) return Number(value) * 1000; // 秒
  return null;
}

/**
 * 优先读取已确认的 WS 字段：serverTimestamp（纳秒）和 timestamp（毫秒）。
 * 同时保留 serverTime/clientTime 等旧命名的兼容。
 * @param {object} obj
 */
function pickServerClientTime(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const confirmedServerTime = toMs(obj.serverTimestamp ?? obj.server_timestamp);
  const confirmedClientTime = toMs(obj.timestamp);
  if (confirmedServerTime !== null && confirmedClientTime !== null) {
    return { serverTime: confirmedServerTime, clientTime: confirmedClientTime };
  }
  let serverTime = confirmedServerTime;
  let clientTime = null;
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value && typeof value === 'object') {
      // extra_info 里可能再包一层
      const nested = pickServerClientTime(value);
      if (nested) {
        serverTime = serverTime ?? nested.serverTime;
        clientTime = clientTime ?? nested.clientTime;
      }
      return;
    }
    const lower = key.toLowerCase();
    if (serverTime === null && lower.includes('server')) {
      serverTime = toMs(value);
    } else if (clientTime === null && (lower.includes('client') || lower.includes('vehicle'))) {
      clientTime = toMs(value);
    }
  });
  if (serverTime === null || clientTime === null) return null;
  return { serverTime, clientTime };
}

/**
 * 解析 ws FrameData 的 extra_info。
 * @param {string} raw
 * @returns {{serverTime:number, clientTime:number}|null}
 */
export function parseExtraInfo(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let obj = null;
  try {
    obj = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  return pickServerClientTime(obj);
}

function addSample(store, serverTime, clientTime) {
  if (!serverTime || !clientTime) return false;
  const previous = store.get(serverTime);
  if (previous?.clientTime === clientTime) return false;
  store.set(serverTime, { serverTime, clientTime, offset: serverTime - clientTime });
  return true;
}

/** 合并下载批次的刷新，但即使偏差相同也通知，保证最近采样和来源更新 */
function scheduleSampleBroadcast() {
  if (sampleBroadcastTimer !== null) return;
  sampleBroadcastTimer = setTimeout(() => {
    sampleBroadcastTimer = null;
    offsetSampleVersion.value += 1;
  }, 100);
}

/**
 * ws 每帧调用：从 extra_info 里取一条 offset 采样。
 * @param {string} raw FrameData.extra_info
 */
export function collectExtraInfoSample(raw) {
  if (!raw) return;
  if (extraInfoLogCount < EXTRA_INFO_LOG_LIMIT) {
    extraInfoLogCount += 1;
    extraInfoRawSamples.push(raw);
    // 保留少量原始样本，便于核对 serverTimestamp/timestamp
    console.log('[timeAlign] ws extra_info 原始内容:', raw);
  }
  const sample = parseExtraInfo(raw);
  if (!sample) return;
  if (addSample(wsSamples, sample.serverTime, sample.clientTime)) {
    wsSorted = null;
    scheduleSampleBroadcast();
  }
}

/**
 * getPlayInfo 返回后调用：用 statusInfos 建立兜底的 offset 采样。
 * @param {Array<any>} statusInfos
 */
export function setStatusInfoOffsetSamples(statusInfos) {
  httpSamples.clear();
  httpSorted = null;
  if (Array.isArray(statusInfos)) {
    statusInfos.forEach((item) => {
      const serverTime = toMs(item?.extras?.serverTimestamp ?? item?.extras?.server_timestamp);
      const clientTime = toMs(item?.msgTime);
      addSample(httpSamples, serverTime, clientTime);
    });
  }
  // 替换或清空 HTTP 采样后都刷新，不能只比较某条采样的 offset
  offsetSampleVersion.value += 1;
}

/** 切包/切时间区间时清空采样 */
export function clearOffsetSamples() {
  wsSamples.clear();
  httpSamples.clear();
  wsSorted = null;
  httpSorted = null;
  if (sampleBroadcastTimer !== null) {
    clearTimeout(sampleBroadcastTimer);
    sampleBroadcastTimer = null;
  }
  extraInfoLogCount = 0;
  extraInfoRawSamples.length = 0;
  offsetSampleVersion.value += 1;
}

function getSorted(store, cache, setCache) {
  if (cache) return cache;
  const list = Array.from(store.values()).sort((a, b) => a.serverTime - b.serverTime);
  setCache(list);
  return list;
}

/** 在按 serverTime 升序的采样里找离 target 最近的一条 */
function findNearest(list, target) {
  if (!list.length) return null;
  let low = 0;
  let high = list.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (list[mid].serverTime < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  const candidate = list[low];
  const prev = list[low - 1];
  if (prev && Math.abs(prev.serverTime - target) <= Math.abs(candidate.serverTime - target)) {
    return prev;
  }
  return candidate;
}

/**
 * 取某个服务端时间点附近的时钟偏差。ws 采样优先，距离过远或没有时回退 http 采样。
 * @param {number} serverTimeMs
 * @returns {{offset:number, serverTime:number, clientTime:number, source:'ws'|'http'}|null}
 */
export function getTimeOffset(serverTimeMs) {
  if (!serverTimeMs) return null;
  const wsList = getSorted(wsSamples, wsSorted, (v) => (wsSorted = v));
  const wsHit = findNearest(wsList, serverTimeMs);
  if (wsHit && Math.abs(wsHit.serverTime - serverTimeMs) <= WS_SAMPLE_MAX_DISTANCE) {
    return { ...wsHit, source: 'ws' };
  }
  const httpList = getSorted(httpSamples, httpSorted, (v) => (httpSorted = v));
  const httpHit = findNearest(httpList, serverTimeMs);
  if (httpHit) {
    return { ...httpHit, source: 'http' };
  }
  return wsHit ? { ...wsHit, source: 'ws' } : null;
}

/**
 * 把服务端时钟的时间戳换算成播放轴(车端时钟)上的位置：ts = serverTime - (serverTime - clientTime)
 * @param {number} serverTimeMs 毫秒
 * @returns {{ts:number, offset:number, sampleServerTime:number|null, sampleClientTime:number|null, source:string|null}}
 */
export function alignServerTimeToClient(serverTimeMs) {
  const hit = getTimeOffset(serverTimeMs);
  if (!hit) {
    return {
      ts: serverTimeMs,
      offset: 0,
      sampleServerTime: null,
      sampleClientTime: null,
      source: null,
    };
  }
  return {
    ts: serverTimeMs - hit.offset,
    offset: hit.offset,
    sampleServerTime: hit.serverTime,
    sampleClientTime: hit.clientTime,
    source: hit.source,
  };
}

if (typeof window !== 'undefined') {
  // 调试入口：window.__timeAlign.dump() / window.__timeAlign.getTimeOffset(ts)
  window.__timeAlign = {
    getTimeOffset,
    alignServerTimeToClient,
    parseExtraInfo,
    dump() {
      const ws = Array.from(wsSamples.values());
      const http = Array.from(httpSamples.values());
      console.log('[timeAlign] ws 采样数:', ws.length, ws.slice(0, 5));
      console.log('[timeAlign] http 采样数:', http.length, http.slice(0, 5));
      console.log('[timeAlign] extra_info 原始样本:', extraInfoRawSamples);
      return { ws, http, extraInfoRawSamples };
    },
  };
}
